import { useRef, useState } from 'react';
import { clamp } from 'lodash';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import type { Milliseconds, OverlayItem, Pixels } from '@/redux/slices/packagingSlice.types';
import { resizeOverlay } from '@/redux/slices/packagingTimelineSlice';
import { setIsEditingTimeline } from '@/redux/slices/packagingSlice';
import { selectPackagingTimelineFrameDuration, selectPackagingTimelineScale } from '@/redux/selectors/packagingSelectors';
import { selectOverlayLayerByIndex, selectTimelineFramesCount } from '@/redux/selectors/packagingTimelineSelectors';
import { MILLISECONDS_PER_PIXEL } from '@/components/Packaging/constants';
import { findNextOverlayInLayer } from '@/components/Packaging/utils';

interface Params {
  layerIndex: number;
  overlay: OverlayItem;
  isOutsideTimeline: boolean;
}

export const useResizableOverlay = (params: Params) => {
  const { layerIndex, overlay, isOutsideTimeline } = params;

  const dispatch = useAppDispatch();
  const scale = useAppSelector(selectPackagingTimelineScale);
  const timelineFramesCount = useAppSelector(selectTimelineFramesCount);
  const currentOverlayLayer = useAppSelector((state) => selectOverlayLayerByIndex(state, layerIndex));
  const frameDuration = useAppSelector(selectPackagingTimelineFrameDuration);
  const [isResizing, setIsResizing] = useState(false);
  const offset = useRef<Pixels>(0); // number of pixels user moved the mouse
  // `endPositionInternal` stores the overlay's duration value during the resizing. When not resizing `endPositionInternal` is null.
  const [endPositionInternal, setEndPositionInternal] = useState<Milliseconds | null>(null);
  // `nextOverlayRef` is needed to limit resizing of the overlay to avoid overlapping with the next overlay after this overlay
  const nextOverlayRef = useRef<OverlayItem | null>(null);

  const handleResizeStart = () => {
    setIsResizing(true);
    dispatch(setIsEditingTimeline(true));
    nextOverlayRef.current = findNextOverlayInLayer(currentOverlayLayer, overlay);
  };

  const handleResize = (delta: { x: number }) => {
    if (isOutsideTimeline) {
      return;
    }

    offset.current += delta.x;
    const end = overlay.end;
    const newEnd = Math.floor(end + (offset.current * MILLISECONDS_PER_PIXEL) / scale / frameDuration);
    const minEnd = overlay.start + 1;
    const maxEnd = Math.min(timelineFramesCount, nextOverlayRef.current !== null ? nextOverlayRef.current.start : Infinity);

    setEndPositionInternal(clamp(newEnd, minEnd, maxEnd));
  };

  const handleResizeEnd = () => {
    setIsResizing(false);
    offset.current = 0;
    dispatch(setIsEditingTimeline(false));

    if (endPositionInternal) {
      dispatch(
        resizeOverlay({
          overlayId: overlay.draggableId,
          layerIndex,
          end: endPositionInternal
        })
      );
    }

    setEndPositionInternal(null);
  };

  return {
    isResizing,
    endPositionInternal,
    handleResizeStart,
    handleResize,
    handleResizeEnd
  };
};
